/**
 * @file     loadLocaleBundle.ts
 * @readme   ./CLAUDE.md
 * @purpose  Async loader that dynamically imports a language's translation and page-content bundles in parallel, enabling code-split locale delivery.
 */
import type { AppLanguage } from './languages';
import { enCore } from './resources/en/core';

type TranslationBundle = {
  translation: Record<string, unknown>;
  pageContent?: Record<string, unknown>;
};

const createLocalizedBundleLoader = <
  TTranslationModule extends Record<string, Record<string, unknown>>,
  TPageContentModule extends Record<string, Record<string, unknown>>,
>(
  loadTranslation: () => Promise<TTranslationModule>,
  translationExport: keyof TTranslationModule,
  loadPageContent: () => Promise<TPageContentModule>,
  pageContentExport: keyof TPageContentModule,
) => {
  return async (): Promise<TranslationBundle> => {
    const [translationModule, pageContentModule] = await Promise.all([
      loadTranslation(),
      loadPageContent(),
    ]);

    return {
      translation: translationModule[translationExport],
      pageContent: pageContentModule[pageContentExport],
    };
  };
};

const loadLocalizedBundleByLanguage: Record<
  Exclude<AppLanguage, 'en'>,
  () => Promise<TranslationBundle>
> = {
  fr: createLocalizedBundleLoader(
    () => import('./resources/fr'),
    'fr',
    () => import('./resources/pageContentTranslations/fr'),
    'frPageContentTranslations',
  ),
  hi: createLocalizedBundleLoader(
    () => import('./resources/hi'),
    'hi',
    () => import('./resources/pageContentTranslations/hi'),
    'hiPageContentTranslations',
  ),
  fa: createLocalizedBundleLoader(
    () => import('./resources/fa'),
    'fa',
    () => import('./resources/pageContentTranslations/fa'),
    'faPageContentTranslations',
  ),
  ar: createLocalizedBundleLoader(
    () => import('./resources/ar'),
    'ar',
    () => import('./resources/pageContentTranslations/ar'),
    'arPageContentTranslations',
  ),
  pt: createLocalizedBundleLoader(
    () => import('./resources/pt'),
    'pt',
    () => import('./resources/pageContentTranslations/pt'),
    'ptPageContentTranslations',
  ),
  es: createLocalizedBundleLoader(
    () => import('./resources/es'),
    'es',
    () => import('./resources/pageContentTranslations/es'),
    'esPageContentTranslations',
  ),
  ru: createLocalizedBundleLoader(
    () => import('./resources/ru'),
    'ru',
    () => import('./resources/pageContentTranslations/ru'),
    'ruPageContentTranslations',
  ),
  pa: createLocalizedBundleLoader(
    () => import('./resources/pa'),
    'pa',
    () => import('./resources/pageContentTranslations/pa'),
    'paPageContentTranslations',
  ),
  ur: createLocalizedBundleLoader(
    () => import('./resources/ur'),
    'ur',
    () => import('./resources/pageContentTranslations/ur'),
    'urPageContentTranslations',
  ),
};

export const loadLocaleBundle = async (
  language: AppLanguage,
): Promise<TranslationBundle> => {
  if (language === 'en') {
    return {
      translation: enCore,
    };
  }

  return loadLocalizedBundleByLanguage[language]();
};
